import { IconAlertTriangle, IconCheck, IconClock, IconX } from "@tabler/icons-react";
import cn from "classnames";
import ExternalPrintFormDownloadBtn from "components/shared/externalPrintFormDownloadBtn";
import InlineBtn from "components/shared/inlineBtn";
import QrCodesDownloadBtn from "components/shared/qrCodesDownloadBtn";
import {
    useWaybillArchiveOperation,
    WaybillArchiveAction,
    WaybillArchiveBadge,
} from "components/waybills/archiveStatusAction";
import { WaybillUnloadComment } from "components/waybills/content";
import { BidWaybillActionBadges } from "components/waybills/bidActionBadges";
import { WaybillExternalWarnings } from "components/waybills/externalWarnings";
import { WaybillTitleFileView } from "components/waybills/titleFileView";
import {
    WaybillTitleRejection,
    WaybillTitleSignatureWarning,
    WaybillTitleStatusBadge,
} from "components/waybills/titleRejection";
import { dateTimeToView } from "helpers/date";
import {
    bidStatusesEnum,
    waybillStateFlags,
    waybillStateFlagsEnum,
    waybillTitleStatuses,
    waybillTitleStatusesEnum,
    waybillTitleTypes,
    waybillTitleTypesEnum,
} from "helpers/enums";
import { paths } from "helpers/paths";
import { canDownloadExternalPrintForms } from "helpers/printForms";
import { enumLabel, flagVal, hasValue } from "helpers/system";
import {
    canAcceptWaybillTitleByLogist,
    canArchiveWaybills,
    canDownloadWaybillQrCodes,
    canSignWaybillResponseTitle,
    getWaybillTitleTimelineState,
    hasSignedNextWaybillTitle,
    isWaybillTitleActionBlocked,
    isWaybillDeliveryExternal,
    isWaybillTitleSignatureInvalid,
    isWaybillTitleSigningRetry,
    nextSignedWaybillTitleWarning,
} from "helpers/waybills";
import { useExternalPrintFormDownload } from "hooks/useExternalPrintFormDownload";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPrintFormFromExternal } from "sources/ebills";
import { getListByBid } from "sources/waybills";

const EBILL_TITLE_SIGN_STATUSES = {
    true: "Подписан",
    false: "Ожидает подписания",
};

const DRIVER_ACCEPTABLE_TITLE_TYPES = [waybillTitleTypesEnum.T1, waybillTitleTypesEnum.T3];
const RESPONSE_TITLE_TYPE_BY_SOURCE = {
    [waybillTitleTypesEnum.T1]: waybillTitleTypesEnum.T2,
    [waybillTitleTypesEnum.T3]: waybillTitleTypesEnum.T4,
};
const FAILED_TITLE_STATUSES = [waybillTitleStatusesEnum.Failed, waybillTitleStatusesEnum.FailedToSend];
const TERMINAL_TITLE_STATUSES = [waybillTitleStatusesEnum.Sent, waybillTitleStatusesEnum.Processed];
const RESPONSE_ALLOWED_SOURCE_TITLE_STATUSES = [waybillTitleStatusesEnum.Accepted, waybillTitleStatusesEnum.Processed];
const waybillChangeTitleRequiredVal = flagVal(waybillStateFlags, waybillStateFlagsEnum.ChangeTitleRequired);
const SIGNING_UNAVAILABLE_TOOLTIP = (
    <>
        Подписание титула доступно, когда заявка <br />
        находится в статусе „Начата“ или „Завершена“
    </>
);

/** Return the newest title of the requested type. */
const latestTitleByType = (titles, type) =>
    titles.reduce((latest, title) => (title.type === type && (!latest || title.id > latest.id) ? title : latest), null);

/** Describe whether T8 will be created or the current draft will be regenerated. */
const changeTitleButtonLabel = (t8Title) =>
    isWaybillTitleSignatureInvalid(t8Title)
        ? "Повторно сформировать и подписать Т8"
        : t8Title && !TERMINAL_TITLE_STATUSES.includes(t8Title.status)
          ? "Повторить формирование Т8"
          : "Сформировать Т8";

const titleStatusLabel = (title) => {
    if (isWaybillTitleSignatureInvalid(title)) {
        return enumLabel(waybillTitleStatuses, title.status);
    }
    if (DRIVER_ACCEPTABLE_TITLE_TYPES.includes(title.type)) {
        return title.signed ? "Подтвержден водителем" : "Ожидает подтверждения водителем";
    }
    if (title.status === waybillTitleStatusesEnum.Failed) {
        return "Ошибка формирования";
    }
    if (title.status === waybillTitleStatusesEnum.FailedToSend) {
        return "Ошибка отправки";
    }
    return EBILL_TITLE_SIGN_STATUSES[title.signed];
};

const buildMiniTimeline = (eBill) => {
    const titles = eBill.titles || [];
    const externalDelivery = isWaybillDeliveryExternal(eBill.intStateFlags);
    const hasT3 = titles.some((t) => t.type === waybillTitleTypesEnum.T3);
    const isChangeTitleRequired =
        (eBill.intStateFlags & waybillChangeTitleRequiredVal) === waybillChangeTitleRequiredVal;
    const needsT8 = !externalDelivery && !hasT3 && isChangeTitleRequired;

    const steps = [
        { type: waybillTitleTypesEnum.T1, label: "Т1" },
        { type: waybillTitleTypesEnum.T2, label: "Т2" },
        { type: waybillTitleTypesEnum.T3, label: "Т3" },
        { type: waybillTitleTypesEnum.T4, label: "Т4" },
    ];

    [waybillTitleTypesEnum.T5, waybillTitleTypesEnum.T6, waybillTitleTypesEnum.T7, waybillTitleTypesEnum.T8].forEach(
        (extraType) => {
            if (extraType && titles.some((t) => t.type === extraType)) {
                steps.push({ type: extraType, label: extraType });
            }
        },
    );

    const timeline = steps.map((step) => {
        const title = latestTitleByType(titles, step.type);
        return { type: step.type, label: step.label, status: getWaybillTitleTimelineState(title), title };
    });

    const t8Title = latestTitleByType(titles, waybillTitleTypesEnum.T8);
    const signableTitles = titles.filter(
        (t) => !externalDelivery && canSignWaybillResponseTitle(t, titles) && !isWaybillTitleActionBlocked(t, titles),
    );
    const signableTitle = signableTitles.find(isWaybillTitleSignatureInvalid) || signableTitles[0];

    return {
        number: eBill.billNumber || eBill.id,
        timeline,
        needsT8,
        hasT8: !!t8Title,
        canGenerateT8: !externalDelivery && (needsT8 || isWaybillTitleSignatureInvalid(t8Title)),
        t8Title,
        changeTitleButtonLabel: changeTitleButtonLabel(t8Title),
        signableTitle,
        eBillId: eBill.id,
    };
};

const renderTimelinePill = (step) => {
    const baseClass = "badge b-etrn__pill mr-1 border";
    switch (step.status) {
        case "signed":
            return (
                <span
                    key={step.type}
                    className={`${baseClass} badge-success border-success`}
                    title={`${step.label}: Подписан`}
                >
                    <IconCheck size={11} className="mr-1" />
                    {step.label}
                </span>
            );
        case "pending":
            return (
                <span
                    key={step.type}
                    className={`${baseClass} badge-warning border-warning`}
                    title={`${step.label}: Ожидает действия`}
                >
                    <IconClock size={11} className="mr-1" />
                    {step.label}
                </span>
            );
        case "rejected":
        case "error":
            return (
                <span
                    key={step.type}
                    className={`${baseClass} badge-danger border-danger`}
                    title={`${step.label}: ${
                        step.status === "rejected"
                            ? "Отказ в подписи"
                            : enumLabel(waybillTitleStatuses, step.title.status)
                    }`}
                >
                    <IconX size={11} className="mr-1" />
                    {step.label}
                </span>
            );
        default:
            return (
                <span
                    key={step.type}
                    className={`${baseClass} badge-light text-muted border-secondary`}
                    title={`${step.label}: Отсутствует`}
                >
                    {step.label}
                </span>
            );
    }
};

const getRemarksRows = ({ remarks, type }) => {
    if (!remarks) {
        return [];
    }

    const loadUnloadLabel =
        type === waybillTitleTypesEnum.T3 ? "Факт постановки на выгрузку" : "Факт постановки на погрузку";
    const loadingUnloadingNotesLabel =
        type === waybillTitleTypesEnum.T3 ? "Замечания по выгрузке" : "Замечания по погрузке";

    return [
        {
            label: "Факт прибытия",
            value: remarks.factEnteredAt,
            type: "date",
        },
        {
            label: "Факт убытия",
            value: remarks.factLeftAt,
            type: "date",
        },
        {
            label: loadUnloadLabel,
            value: remarks.factLoadUnloadAt,
            type: "date",
        },
        {
            label: "Замечания по состоянию груза",
            value: remarks.cargoConditionNotes,
        },
        {
            label: "Замечания по массе груза",
            value: remarks.cargoWeightNotes,
        },
        {
            label: "Замечания по количеству грузовых мест",
            value: remarks.cargoPlacesNotes,
        },
        {
            label: loadingUnloadingNotesLabel,
            value: remarks.loadingUnloadingNotes,
        },
        {
            label: "Примечание",
            value: remarks.note,
        },
    ].filter(({ value }) => hasValue(value));
};

const BidEBills = ({ bid, modal, getItem, powerOfAttorneys, roles, profile }) => {
    const { download: downloadPrintForm, isDownloading: isWaybillDownloading } = useExternalPrintFormDownload({
        documentType: "waybill",
        requestFn: getPrintFormFromExternal,
    });
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [archiveAccess, setArchiveAccess] = useState(null);
    const waybills = bid.waybills || [];
    const canArchive = canArchiveWaybills(roles, profile.availableFeatureModules);
    const canDownloadPdf = canDownloadExternalPrintForms(roles);
    const archiveOperation = useWaybillArchiveOperation(getItem);
    const userId = profile.id;
    const signingDisabled = ![bidStatusesEnum.Started, bidStatusesEnum.Done].includes(bid.status);
    const signingTooltip = signingDisabled ? SIGNING_UNAVAILABLE_TOOLTIP : undefined;

    useEffect(() => {
        if (!canArchive || !waybills.length) {
            return;
        }
        let cancelled = false;
        getListByBid(bid.id)
            .then((response) => {
                if (!cancelled) {
                    setArchiveAccess({
                        bidId: bid.id,
                        userId,
                        waybillIds: Array.isArray(response) ? response.map(({ id }) => id) : [],
                    });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setArchiveAccess(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [bid.id, canArchive, userId, waybills.length]);

    const canArchiveDocument = (waybillId) =>
        canArchive &&
        archiveAccess?.bidId === bid.id &&
        archiveAccess?.userId === userId &&
        archiveAccess.waybillIds.includes(waybillId);

    if (!waybills.length) {
        return <BidWaybillActionBadges intStateFlags={bid.intStateFlags} />;
    }

    const canDownloadQrCodes =
        waybills.some((eBill) => canDownloadWaybillQrCodes(eBill.titles || [])) &&
        waybills.every(
            (eBill) => !isWaybillTitleSignatureInvalid(latestTitleByType(eBill.titles || [], waybillTitleTypesEnum.T2)),
        );

    const acceptWaybillTitle = (waybillId, eBillTitle) => {
        modal({
            name: "acceptWaybillTitle",
            title:
                eBillTitle.status === waybillTitleStatusesEnum.FailedToSend
                    ? `Повторная отправка замечаний по ${eBillTitle.type}`
                    : `Подтверждение ${enumLabel(waybillTitleTypes, eBillTitle.type)} от имени водителя`,
            windowMod: "wide",
            waybillId,
            driverId: bid.driver?.id,
            waybillTitle: eBillTitle,
            callback: getItem,
        });
    };
    const generateResponseTitle = (waybillId, sourceTitle, retry) => {
        const responseType = RESPONSE_TITLE_TYPE_BY_SOURCE[sourceTitle.type];
        modal({
            name: "signWaybillResponseTitle",
            title: retry
                ? `Повторное формирование и подписание ${responseType} ЭТрН`
                : `Формирование и подписание ${responseType} ЭТрН`,
            windowMod: "wide",
            waybillId,
            sourceTitleId: sourceTitle.id,
            powerOfAttorneys,
            callback: getItem,
        });
    };
    const signResponseTitle = (waybillId, title) => {
        const retrySend = title.status === waybillTitleStatusesEnum.FailedToSend;
        modal({
            name: "signFile",
            title: isWaybillTitleSignatureInvalid(title)
                ? `Повторное подписание ${title.type} ЭТрН`
                : retrySend
                  ? `Повторная отправка ${title.type} ЭТрН`
                  : `Подписание ${title.type} ЭТрН`,
            windowMod: "wide",
            waybillId,
            titleId: title.id,
            callback: getItem,
        });
    };
    const generateChangeTitleModal = (waybillId) => {
        modal({
            name: "generateWaybillChangeTitle",
            title: "Формирование и подписание Т8 ЭТрН",
            windowMod: "wide",
            waybillId,
            powerOfAttorneys,
            callback: getItem,
        });
    };

    return (
        <>
            <div
                className="show-bid__section-header d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => setIsCollapsed((prev) => !prev)}
            >
                <span>Статус подписания ЭТрН ({waybills.length})</span>
                <i
                    className={cn("icon-uEA72-chevron-down b-etrn__chevron", {
                        "b-etrn__chevron--collapsed": isCollapsed,
                    })}
                />
            </div>
            {isCollapsed ? (
                <div className="b-etrn pt-2">
                    <div className="d-flex flex-wrap">
                        <BidWaybillActionBadges intStateFlags={bid.intStateFlags} />
                    </div>
                    {waybills.map((eBill) => {
                        const summary = buildMiniTimeline(eBill);
                        return (
                            <div key={eBill.id} className="b-etrn__card mb-2">
                                <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center flex-wrap">
                                        <span className="font-weight-bold mr-3">
                                            ЭТрН №{summary.number}
                                            <WaybillArchiveBadge intStateFlags={eBill.intStateFlags} />
                                        </span>
                                        <div className="d-flex align-items-center">
                                            {summary.timeline.map(renderTimelinePill)}
                                            {summary.needsT8 && !summary.hasT8 && (
                                                <span
                                                    className="badge b-etrn__pill badge-warning border border-warning ml-1"
                                                    title="Требуется сформировать Т8"
                                                >
                                                    <IconAlertTriangle size={11} className="mr-1" />
                                                    Т8
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center pt-2">
                                        <div className="mr-2">
                                            <WaybillArchiveAction
                                                waybillId={eBill.id}
                                                intStateFlags={eBill.intStateFlags}
                                                canArchive={canArchiveDocument(eBill.id)}
                                                modal={modal}
                                                operation={archiveOperation}
                                            />
                                        </div>
                                        {summary.canGenerateT8 && (
                                            <InlineBtn
                                                value={summary.changeTitleButtonLabel}
                                                cname="usb-key"
                                                action={() => generateChangeTitleModal(eBill.id)}
                                                disabled={
                                                    signingDisabled && !isWaybillTitleSignatureInvalid(summary.t8Title)
                                                }
                                                tooltip={
                                                    isWaybillTitleSignatureInvalid(summary.t8Title)
                                                        ? undefined
                                                        : signingTooltip
                                                }
                                            />
                                        )}
                                        {summary.signableTitle &&
                                            (!summary.needsT8 ||
                                                isWaybillTitleSignatureInvalid(summary.signableTitle)) && (
                                                <InlineBtn
                                                    value={
                                                        isWaybillTitleSignatureInvalid(summary.signableTitle)
                                                            ? "Подписать повторно"
                                                            : `Подписать ${summary.signableTitle.type}`
                                                    }
                                                    cname="usb-key"
                                                    action={() => signResponseTitle(eBill.id, summary.signableTitle)}
                                                    disabled={
                                                        signingDisabled &&
                                                        !isWaybillTitleSigningRetry(summary.signableTitle)
                                                    }
                                                    tooltip={
                                                        signingDisabled &&
                                                        !isWaybillTitleSigningRetry(summary.signableTitle)
                                                            ? SIGNING_UNAVAILABLE_TOOLTIP
                                                            : undefined
                                                    }
                                                />
                                            )}
                                    </div>
                                </div>
                                <WaybillExternalWarnings waybill={eBill} />
                                {isWaybillDeliveryExternal(eBill.intStateFlags) &&
                                    (eBill.titles || []).map((title) => (
                                        <WaybillTitleFileView key={title.id} waybillId={eBill.id} title={title} />
                                    ))}
                                <WaybillUnloadComment unloadComment={eBill.unloadComment} />
                                <div className="mt-1">
                                    <Link to={`${paths.waybillsShow(eBill.id)}#matching-history`} className="small">
                                        История сопоставления
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                    {canDownloadQrCodes && (
                        <div className="d-flex justify-content-end pb-2">
                            <QrCodesDownloadBtn bidId={bid.id} />
                        </div>
                    )}
                    <div className="text-center pt-2">
                        <span
                            className="text-primary font-weight-bold cursor-pointer"
                            onClick={() => setIsCollapsed(false)}
                        >
                            Показать подробную историю титулов &darr;
                        </span>
                    </div>
                </div>
            ) : (
                <div className="b-etrn pt-2">
                    <div className="d-flex flex-wrap">
                        <BidWaybillActionBadges intStateFlags={bid.intStateFlags} />
                    </div>
                    {waybills.map((eBill) => {
                        const titles = eBill.titles || [];
                        const externalDelivery = isWaybillDeliveryExternal(eBill.intStateFlags);
                        const hasT3 = titles.some((t) => t.type === waybillTitleTypesEnum.T3);
                        const t8Title = latestTitleByType(titles, waybillTitleTypesEnum.T8);
                        const isChangeTitleRequired =
                            (eBill.intStateFlags & waybillChangeTitleRequiredVal) === waybillChangeTitleRequiredVal;
                        const eBillNeedsT8 = !hasT3 && isChangeTitleRequired;

                        return (
                            <div key={eBill.id} className="b-etrn__card">
                                <div className="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                                    <span className="font-weight-bold">
                                        ЭТрН №{eBill.billNumber || eBill.id}
                                        <WaybillArchiveBadge intStateFlags={eBill.intStateFlags} />
                                    </span>
                                    <Link to={`${paths.waybillsShow(eBill.id)}#matching-history`} className="small">
                                        История сопоставления
                                    </Link>
                                </div>
                                <div className="d-flex justify-content-end mb-2">
                                    <WaybillArchiveAction
                                        waybillId={eBill.id}
                                        intStateFlags={eBill.intStateFlags}
                                        canArchive={canArchiveDocument(eBill.id)}
                                        modal={modal}
                                        operation={archiveOperation}
                                    />
                                </div>
                                <WaybillExternalWarnings waybill={eBill} />
                                <WaybillUnloadComment unloadComment={eBill.unloadComment} />
                                {!externalDelivery && (eBillNeedsT8 || isWaybillTitleSignatureInvalid(t8Title)) && (
                                    <div className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light border border-warning rounded">
                                        <span
                                            className={`badge ${isWaybillTitleSignatureInvalid(t8Title) ? "badge-danger" : "badge-warning"}`}
                                        >
                                            {isWaybillTitleSignatureInvalid(t8Title)
                                                ? `${titleStatusLabel(t8Title)} ${t8Title.type.replace("T", "Т")}`
                                                : "Требуется сформировать Т8"}
                                        </span>
                                        <InlineBtn
                                            value={changeTitleButtonLabel(t8Title)}
                                            cname="usb-key"
                                            action={() => generateChangeTitleModal(eBill.id)}
                                            disabled={signingDisabled && !isWaybillTitleSignatureInvalid(t8Title)}
                                            tooltip={
                                                isWaybillTitleSignatureInvalid(t8Title) ? undefined : signingTooltip
                                            }
                                        />
                                    </div>
                                )}
                                {(eBill.titles || []).map((eBillTitle, i) => {
                                    const actionBlocked =
                                        externalDelivery || isWaybillTitleActionBlocked(eBillTitle, titles);
                                    const nextTitleSigned = hasSignedNextWaybillTitle(eBillTitle, titles);
                                    const canAcceptByLogist =
                                        !externalDelivery && canAcceptWaybillTitleByLogist(eBillTitle, titles);
                                    const retryRemarks = eBillTitle.status === waybillTitleStatusesEnum.FailedToSend;
                                    const remarksRows = getRemarksRows(eBillTitle);
                                    const responseType = RESPONSE_TITLE_TYPE_BY_SOURCE[eBillTitle.type];
                                    const responseTitle = responseType ? latestTitleByType(titles, responseType) : null;
                                    const canGenerateResponse =
                                        !!responseType &&
                                        !actionBlocked &&
                                        RESPONSE_ALLOWED_SOURCE_TITLE_STATUSES.includes(eBillTitle.status) &&
                                        (!responseTitle || !TERMINAL_TITLE_STATUSES.includes(responseTitle.status));
                                    const canSignResponse =
                                        !actionBlocked && canSignWaybillResponseTitle(eBillTitle, titles);
                                    const showTitleActions =
                                        canAcceptByLogist ||
                                        canGenerateResponse ||
                                        (canDownloadPdf && eBillTitle.type === waybillTitleTypesEnum.T1);

                                    return (
                                        <div
                                            key={eBillTitle.id}
                                            className={
                                                i !== (eBill.titles || []).length - 1
                                                    ? "mb-2 pb-2 border-bottom border-secondary"
                                                    : ""
                                            }
                                        >
                                            <div className="d-flex justify-content-between align-items-baseline">
                                                <b>{enumLabel(waybillTitleTypes, eBillTitle.type)}.</b>
                                                <WaybillTitleStatusBadge
                                                    title={eBillTitle}
                                                    label={titleStatusLabel(eBillTitle)}
                                                    failed={FAILED_TITLE_STATUSES.includes(eBillTitle.status)}
                                                />
                                            </div>
                                            <WaybillTitleRejection rejectionReason={eBillTitle.rejectionReason} />
                                            <WaybillTitleSignatureWarning title={eBillTitle} />
                                            {externalDelivery && (
                                                <WaybillTitleFileView waybillId={eBill.id} title={eBillTitle} />
                                            )}
                                            <div>
                                                <b className="text-muted">Получен:</b>{" "}
                                                {dateTimeToView(eBillTitle.receivedAt)}
                                            </div>
                                            {eBillTitle.signedAt && !isWaybillTitleSignatureInvalid(eBillTitle) && (
                                                <div>
                                                    <b className="text-muted">Подписан:</b>{" "}
                                                    {dateTimeToView(eBillTitle.signedAt)}
                                                </div>
                                            )}
                                            {remarksRows.length > 0 && (
                                                <div className="b-etrn__remarks">
                                                    <div className="b-etrn__remarks-title">Замечания водителя</div>
                                                    {remarksRows.map(({ label, value, type }) => (
                                                        <div key={label} className="b-etrn__remarks-row">
                                                            <b className="text-muted">{label}:</b>{" "}
                                                            <span>
                                                                {type === "date" ? dateTimeToView(value) : value}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {showTitleActions && (
                                                <div className="d-flex justify-content-end pt-2 flex-wrap">
                                                    {canAcceptByLogist && (
                                                        <div className="mb-1">
                                                            <InlineBtn
                                                                value={
                                                                    retryRemarks
                                                                        ? "Повторить отправку замечаний"
                                                                        : "Подтвердить от имени водителя"
                                                                }
                                                                cname="checkmark"
                                                                action={() => acceptWaybillTitle(eBill.id, eBillTitle)}
                                                                disabled={
                                                                    (signingDisabled && !retryRemarks) ||
                                                                    nextTitleSigned
                                                                }
                                                                tooltip={
                                                                    nextTitleSigned
                                                                        ? nextSignedWaybillTitleWarning
                                                                        : retryRemarks
                                                                          ? undefined
                                                                          : signingTooltip
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                    {canGenerateResponse && (
                                                        <div className="mb-1">
                                                            <InlineBtn
                                                                value={
                                                                    responseTitle
                                                                        ? isWaybillTitleSignatureInvalid(responseTitle)
                                                                            ? `Повторно сформировать и подписать ${responseType}`
                                                                            : `Повторить формирование и подписание ${responseType}`
                                                                        : `Сформировать и подписать ${responseType}`
                                                                }
                                                                cname="usb-key"
                                                                action={() =>
                                                                    generateResponseTitle(
                                                                        eBill.id,
                                                                        eBillTitle,
                                                                        !!responseTitle,
                                                                    )
                                                                }
                                                                disabled={
                                                                    signingDisabled &&
                                                                    !isWaybillTitleSignatureInvalid(responseTitle)
                                                                }
                                                                tooltip={
                                                                    isWaybillTitleSignatureInvalid(responseTitle)
                                                                        ? undefined
                                                                        : signingTooltip
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                    {canDownloadPdf && eBillTitle.type === waybillTitleTypesEnum.T1 && (
                                                        <ExternalPrintFormDownloadBtn
                                                            documentType="waybill"
                                                            onClick={() => downloadPrintForm(eBill.id)}
                                                            loading={isWaybillDownloading(eBill.id)}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {!externalDelivery &&
                                                eBillTitle.type === waybillTitleTypesEnum.T5 &&
                                                !eBillTitle.signed && (
                                                    <div className="d-flex justify-content-end pt-2">
                                                        <InlineBtn
                                                            value="Проверить и подписать"
                                                            cname="usb-key"
                                                            action={() =>
                                                                modal({
                                                                    name: "checkAndSignFile",
                                                                    title: (
                                                                        <h5>Проверьте данные перед их подписанием.</h5>
                                                                    ),
                                                                    windowMod: "wide",
                                                                    waybillId: eBill.id,
                                                                    titleId: eBillTitle.id,
                                                                    callback: getItem,
                                                                })
                                                            }
                                                            disabled={signingDisabled}
                                                            tooltip={signingTooltip}
                                                        />
                                                    </div>
                                                )}
                                            {canSignResponse && (
                                                <div className="d-flex justify-content-end pt-2">
                                                    <InlineBtn
                                                        value={
                                                            isWaybillTitleSignatureInvalid(eBillTitle)
                                                                ? "Подписать повторно"
                                                                : eBillTitle.status ===
                                                                    waybillTitleStatusesEnum.FailedToSend
                                                                  ? "Повторить отправку"
                                                                  : "Подписать"
                                                        }
                                                        cname="usb-key"
                                                        action={() => signResponseTitle(eBill.id, eBillTitle)}
                                                        disabled={
                                                            signingDisabled && !isWaybillTitleSigningRetry(eBillTitle)
                                                        }
                                                        tooltip={
                                                            signingDisabled && !isWaybillTitleSigningRetry(eBillTitle)
                                                                ? SIGNING_UNAVAILABLE_TOOLTIP
                                                                : undefined
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                    {canDownloadQrCodes && (
                        <div className="d-flex justify-content-end">
                            <QrCodesDownloadBtn bidId={bid.id} />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default BidEBills;
